import { PersistenceState } from '@/ui'
import { CheckIcon, Loader2Icon } from 'lucide-react'
import { AnimatePresence, motion } from 'motion/react'
import { useEffect, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'

interface SaveStatusProps {
	status: PersistenceState
}

export function SaveStatus({ status }: SaveStatusProps) {
	const { t } = useTranslation('common')
	const [showSaved, setShowSaved] = useState(false)
	const savedTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null)
	const prevStatusRef = useRef<PersistenceState>('idle')

	useEffect(() => {
		const prev = prevStatusRef.current
		prevStatusRef.current = status

		if (prev === 'saving' && status === 'idle') {
			setShowSaved(true)
			if (savedTimerRef.current) { clearTimeout(savedTimerRef.current) }
			savedTimerRef.current = setTimeout(() => setShowSaved(false), 2500)
		}
		if (status !== 'idle') {
			setShowSaved(false)
			if (savedTimerRef.current) { clearTimeout(savedTimerRef.current) }
		}

		return () => {
			if (savedTimerRef.current) { clearTimeout(savedTimerRef.current) }
		}
	}, [status])

	const isSaving = status === 'saving' || status === 'debouncing'

	return (
		<AnimatePresence mode="wait">
			{isSaving && (
				<motion.div
					key="saving"
					initial={{ opacity: 0 }}
					animate={{ opacity: 1 }}
					exit={{ opacity: 0 }}
					transition={{ duration: 0.15 }}
					className="flex items-center gap-1.5 text-sm text-grey-400"
				>
					<Loader2Icon className="size-3.5 animate-spin" />
					{t('branding.saving')}
				</motion.div>
			)}
			{showSaved && (
				<motion.div
					key="saved"
					initial={{ opacity: 0, y: 4 }}
					animate={{ opacity: 1, y: 0 }}
					exit={{ opacity: 0 }}
					transition={{ duration: 0.2 }}
					className="flex items-center gap-1.5 text-sm text-green-600"
				>
					<CheckIcon className="size-3.5" strokeWidth={2.5} />
					{t('branding.saved')}
				</motion.div>
			)}
		</AnimatePresence>
	)
}
